import dayjs from 'dayjs'
import { IRaffleWinner } from 'types/prize-draw/raffles'

/**
 * Formats an ISO date in the offset it was written in, ignoring the viewer's timezone,
 * so the server and client render the same text (no hydration mismatch).
 *
 * @example
 * formatInSourceOffset('2026-10-12T19:00:00+01:00', 'D MMM, HH:mm') // "12 Oct, 19:00"
 */
export const formatInSourceOffset = (isoString: string, format: string): string =>
  dayjs(isoString.replace(/(Z|[+-]\d{2}:?\d{2})$/, '')).format(format)

/**
 * Formats a number or string to 2 decimal places.
 * Primarily used for currency display.
 *
 * @example
 * formatCurrency(1330.5)      // £1,330.50
 * formatCurrency(1000000)     // £1,000,000.00
 * formatCurrency('12345.67')  // £12,345.67
 * formatCurrency(1.2)         // £1.20
 * formatCurrency(0.1) // "0.10"
 * formatCurrency("5") // "5.00"
 * formatCurrency(1800, { trimWholePounds: true }) // £1,800
 * formatCurrency(30.5, { trimWholePounds: true })  // £30.50
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  { trimWholePounds = false } = {},
): string => {
  if (amount === null || amount === undefined) {
    return '£0.00'
  }

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount

  if (Number.isNaN(numericAmount)) {
    return '£0.00'
  }

  const isWholePounds = Math.round(numericAmount * 100) % 100 === 0
  const fractionDigits = trimWholePounds && isWholePounds ? 0 : 2

  return `£${numericAmount.toLocaleString('en-GB', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

/**
 * Pads a time unit to two digits for countdowns.
 *
 * @example
 * padTime(5)  // "05"
 * padTime(42) // "42"
 */
export const padTime = (value: number): string => String(value).padStart(2, '0')

export const formatWinnerName = ({
  first_name,
  last_name,
  nickname,
}: Pick<IRaffleWinner, 'first_name' | 'last_name' | 'nickname'>): string =>
  first_name && last_name ? `${first_name} ${last_name}` : nickname
